const qs = require('querystring')

const _isNil = require('lodash.isnil')
const _cloneDeep = require('lodash.clonedeep')
const _partition = require('lodash.partition')

const filteredPattern = 'XXXXX'

/**
 * A list of all keys that we should always try and remove from request logs.
 *
 * @private
 */
let keysBlacklist = new Set([
  /**
   * Auth
   */
  'password',
  'new_password',
  'old_password',
  'old_pwd',
  'new_pwd',
  'password_confirmation',
  'client_id',
  'client_secret',
  'access_token',
  'refresh_token',

  /**
   * Payment
   */
  'cc',
  'credit_card_number',
  'card_number',
  'cvv',
  'deposit_info',
  'buyer_info',
])

const qsRegex = /^[\w-]+=[\w-]+(&[\w-]+=[\w-]*?)*?$/

/**
 * @return {Boolean}
 */
const isObject = (val) => !_isNil(val) && val.constructor && (val.constructor === Object)
const isString = (val) => typeof val === 'string'
const isQueryString = (val) => qsRegex.test(val)

/**
 * Accepts a value and returns a sanitized version of that type. Right now, we only have support for
 * sanitizing *Object*. Later, we can support string types through pattern matching.
 *
 * The *last* thing we want is the logger failing, causing the app itself to fail, which is why, as
 * a last resort, we wrap the whole function body in a try/catch.
 */
function sanitize (data) {
  try {
    /**
     * This is a special case, where JSON.parse will not fail on a string value, which we rely on
     * further down. JSON.parse('null') => null.
     */
    if (_isNil(data) || data === 'null') {
      return data
    } else if (isObject(data)) {
      return sanitizeObject(data)
    } else if (isString(data)) {
      return sanitizeString(data)
    }

    return data
  } catch (e) {
    console.error('Internal logger error: ', e)
    return data
  }
}

/**
 * This will try and parse a string into a JSON object and return the same string if it can't be
 * parsed. This should be used when we aren't sure if the data will be given through the use of
 * JSON.stringify, or if a plain object is passed instead.
 *
 * Deep clone to make sure we don't change original structure of reference types.
 *
 * @private
 */
function safeParse (data) {
  data = _cloneDeep(data)

  if (isObject(data)) {
    return data
  }

  try {
    return JSON.parse(data)
  } catch (e) {
    return data
  }
}

/**
 * @private
 */
function sanitizeObject (data) {
  const safeData = safeParse(data)
  const [objectKeys, otherKeys] = _partition(Object.keys(safeData), key => isObject(safeData[key]))

  objectKeys
    .forEach(key => (safeData[key] = sanitizeObject(safeData[key])))

  otherKeys
    .forEach(key => {
      safeData[key] = keysBlacklist.has(key)
        ? filteredPattern
        : sanitize(safeData[key])
    })

  return safeData
}

/**
 * @private
 */
function sanitizeString (data) {
  if (isQueryString(data)) {
    const queryObject = qs.parse(data)

    return qs.stringify(sanitizeObject(queryObject))
  }

  // The JSON spec uses " as the only valid quote char, which means if a string has an inner escaped
  // substring wrapped in that quote char, it will parse it to return the inner string, which would cause this to
  // then go through each letter of the string an try and sanitize that. To avoid that, we check for an escaped string
  // here. This occurs in our products when we do special formatting for search backends like ElasticSearch.
  const isEscapedString = data[0] === '"' && data[data.length - 1] === '"'
  const safeData = safeParse(data)

  // String is not a stringified object
  return isEscapedString || (data === safeData)
    ? data
    : JSON.stringify(sanitizeObject(safeData))
}

sanitize.filteredPattern = filteredPattern
sanitize.addSanitizedKeys = (extraKeys = []) => {
  keysBlacklist = new Set([...keysBlacklist, ...extraKeys])
}

module.exports = sanitize
